import { Flex, LoadingOverlay, Stack } from '@mantine/core'
import { type BlocksContent } from '@strapi/blocks-react-renderer'
import { ReactQuery } from 'cms-api'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { getPublicLayout } from '@public/components/PublicLayout'
import { Box } from 'components/Layout'
import {
  StrapiDoublePaneWidget,
  StrapiHeroWidget,
  StrapiTradeBenefitsWidget,
} from 'components/Widgets'
import { STRAPI_WIDGETS } from 'constants/common'
import { prepareGetStaticNavigationProps } from 'routes/utils/prepareGetStaticNavigationProps'
import { SPACE } from 'Theme'
import { TradeForm } from './TradeForm'

export const Trade = () => {
  const router = useRouter()
  const { data: pageData, isLoading } =
    ReactQuery.useGetExpertTradeApplicationPage({
      populate: {
        widgets: {
          on: {
            [STRAPI_WIDGETS.EXPERT_TRADE_APPLICATION_FORM_WIDGET]: {
              populate: '*',
            },
            [STRAPI_WIDGETS.HERO_WIDGET]: {
              populate: '*',
            },
            [STRAPI_WIDGETS.DOUBLE_PANE_WIDGET]: {
              populate: {
                items: {
                  populate: '*',
                },
                image: {
                  populate: '*',
                },
              },
            },
            [STRAPI_WIDGETS.TRADE_BENEFITS_WIDGET]: {
              populate: '*',
            },
          },
        },
      } as never,
    })

  useEffect(() => {
    const handleHashScroll = () => {
      if (window.location.hash) {
        setTimeout(() => {
          const id = window.location.hash.substring(1)
          const targetElement = document.getElementById(id)

          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }, 1000)
      }
    }

    handleHashScroll()

    // Scroll to the hash when the route changes
    router.events.on('routeChangeComplete', handleHashScroll)

    return () => {
      router.events.off('routeChangeComplete', handleHashScroll)
    }
  }, [router.events, pageData])

  const widgets = pageData?.data?.widgets ?? []

  return (
    <Stack w="100%" gap={8}>
      {isLoading && <LoadingOverlay />}
      {widgets.map((widget) => {
        if (
          // eslint-disable-next-line no-underscore-dangle
          widget.__component ===
          STRAPI_WIDGETS.EXPERT_TRADE_APPLICATION_FORM_WIDGET
        ) {
          return (
            <Box position="relative" key={widget.id}>
              <Box
                position="absolute"
                top={{ MOBILE: '-94px', TABLET: '-60px' }}
                id="form"
              />
              <Flex bg="#F8F8F8" justify="center">
                <Box
                  px={{ MOBILE: SPACE.PX_16, TABLET: SPACE.PX_60 }}
                  py={{ MOBILE: '48px', TABLET: SPACE.PX_80 }}
                >
                  <TradeForm
                    description={widget.description as BlocksContent}
                    consent={widget.consent as BlocksContent}
                  />
                </Box>
              </Flex>
            </Box>
          )
        }
        if (
          // eslint-disable-next-line no-underscore-dangle
          widget.__component === STRAPI_WIDGETS.HERO_WIDGET
        ) {
          return (
            <Box key={widget.id}>
              <StrapiHeroWidget {...widget} />
            </Box>
          )
        }
        // eslint-disable-next-line no-underscore-dangle
        if (widget.__component === STRAPI_WIDGETS.DOUBLE_PANE_WIDGET) {
          return <StrapiDoublePaneWidget key={widget.id} {...widget} />
        }
        // eslint-disable-next-line no-underscore-dangle
        if (widget.__component === STRAPI_WIDGETS.TRADE_BENEFITS_WIDGET) {
          return (
            <Box position="relative" key={widget.id}>
              <Box
                position="absolute"
                top={{ MOBILE: '-94px', TABLET: '-60px' }}
                id="benefits"
              />
              <StrapiTradeBenefitsWidget key={widget.id} {...widget} />
            </Box>
          )
        }

        return null
      })}
    </Stack>
  )
}

export function getStaticProps() {
  return prepareGetStaticNavigationProps()
}

Trade.seoProps = {
  description:
    'Apply to The Expert Trade Program to receive 10% off all purchases from The Expert Collection and The Expert Vintage.',
}

Trade.getLayout = (page) =>
  getPublicLayout(page, {
    isFullWidth: true,
  })
